import { useState } from 'react';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  formatContactMonth,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { WidgetDivider } from '../widgetDivider';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { WidgetNotice } from '../widgetNotice';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';
import { PointsBreakdownLegend } from '../pointsBreakdownLegend';
import { RateChip } from '../rateChip';
import { DownPaymentTooltip } from '../downPaymentTooltip';

type Props = { data: SavingsPointsData };

export function ConsortiumInvestor({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);

  const earned = parseFloat(data.savings_points_earned);
  const bonus = parseFloat(data.bonus_credits_points) || 0;
  const referral = parseFloat(data.referral_credits_points) || 0;
  const base = Math.max(0, earned - bonus - referral);

  const hasBonus = bonus > 0;
  const hasReferral = referral > 0;

  const pointsSegments =
    hasBonus || hasReferral
      ? [
          { flex: base || 1, color: WIDGET_COLORS.green.bar },
          ...(hasBonus ? [{ flex: bonus, color: WIDGET_COLORS.bonus.bar }] : []),
          ...(hasReferral ? [{ flex: referral, color: WIDGET_COLORS.referral.bar }] : []),
        ]
      : [{ flex: 1, color: WIDGET_COLORS.green.bar }];

  const dpTarget = data.additional_downpayment_needed;
  const hasPropertyInfo = !!data.property_value && !!data.deposit_percentage;

  return (
    <>
      <WidgetCardHeader variant="warning" badgeText="Consortium" />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="font-bold" style={{ color: WIDGET_COLORS.green.text }}>
            ✓ {formatPoints(data.savings_points_earned)} of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={100}
        segments={pointsSegments}
        footer={
          <PointsBreakdownLegend
            basePoints={base.toString()}
            bonusPoints={data.bonus_credits_points}
            referralPoints={data.referral_credits_points}
          />
        }
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          hasPropertyInfo ? (
            <DownPaymentTooltip
              propertyValue={data.property_value!}
              depositPercentage={data.deposit_percentage!}
              targetAmount={dpTarget}
            >
              <span className="text-gray-500">
                <strong className="text-gray-800">{formatCurrency('0')}</strong> of{' '}
                {formatCurrency(dpTarget)} target
              </span>
            </DownPaymentTooltip>
          ) : (
            <span className="text-gray-500">
              <strong className="text-gray-800">{formatCurrency('0')}</strong> of{' '}
              {formatCurrency(dpTarget)} target
            </span>
          )
        }
        barPercent={0}
        segments={[{ flex: 1, color: WIDGET_COLORS.amber.bar }]}
      />

      <StatRow
        bg={WIDGET_COLORS.amber.bg}
        dividerColor={WIDGET_COLORS.amber.divider}
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.green.text,
          label: 'Community Points ✓',
          extra: <RateChip totalInvestment={data.total_investment} />,
        }}
        right={{
          value: data.eligibility_month ? formatContactMonth(data.eligibility_month) : '-',
          valueColor: WIDGET_COLORS.amber.text,
          label: (
            <span className="inline-flex items-center uppercase">
              Estimated Eligibility
              <InfoButton
                onClick={() => setInfoOpen(true)}
                style={{ borderColor: WIDGET_COLORS.amber.text, color: WIDGET_COLORS.amber.text }}
              />
            </span>
          ),
          extra: (
            <span
              className="text-[11px] font-bold uppercase tracking-[0.4px]"
              style={{ color: WIDGET_COLORS.amber.text }}
            >
              Waiting for down payment
            </span>
          ),
        }}
      />

      <WidgetNotice variant="warn" icon="alert-triangle">
        Congratulations on collecting all your community points! To be eligible for home finance, you
        will also need to build your GYS Home down payment. We recommend starting your GYS Home
        investment at least <strong>6 months</strong> before your estimated eligibility date.
      </WidgetNotice>

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />
    </>
  );
}
